import type { UI } from '@/v2/types';
import { Flex, Text } from '@qga/roo-ui/components';
import pluralize from 'pluralize';

import { CtaEvent } from '@shared/v2/components/createRoomOptionCTA/types';
import useOtherRoomOptions from '@shared/v2/hooks/useOtherRoomOptions';
import scrollTo from '@shared/v2/utils/scrollIntoView';

import Button from '@/components/Button';
import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
import { useOtherPackageModal } from '@/v2/components/pages/PackageViewV2/contexts/OtherPackageModalContext';
import { useRoomOptionsData } from '@/v2/components/pages/PackageViewV2/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import { usePackagePageV3 } from '@/v2/hooks/featureFlags/packagePageV3/usePackagePageV3';
import RoomList from '../RoomList';

export type OtherRoomsProps = {
  roomOptions: UI.RoomOption[] | undefined | null;
  roomIndexOffset?: number;
};

const OtherRooms = ({ roomOptions, roomIndexOffset }: OtherRoomsProps) => {
  const isPackagePageV3Enabled = usePackagePageV3();
  const { route, isPreSearch } = useRoomOptionsData() || {};

  const { visibleRooms, toggleVisibleRooms, hiddenRoomCount, isExpanded } =
    useOtherRoomOptions({
      roomOptions,
      route,
      isPreSearch,
      isPackagePageV3Enabled,
    });
  const breakpoints = useBreakpoints();
  const isSmallScreen = isScreen(breakpoints)('xs', 'sm');
  const { openTravellers, openMobileSearchPanelModal } =
    useSearchPanelControls();

  const { openOtherPackageModal } = useOtherPackageModal();

  const handleCTAClick = (
    eventName: CtaEvent,
    data: { roomOption: UI.RoomOption; ctaText?: string },
  ) => {
    if (eventName === 'CHANGE_TRAVELLERS') {
      if (isSmallScreen) {
        openMobileSearchPanelModal();
        openTravellers();
      } else {
        scrollTo('#travellers', { offset: -70, behavior: 'smooth' });
        openTravellers();
      }
    } else {
      openOtherPackageModal(data.roomOption, data.ctaText);
    }
  };

  return visibleRooms?.length ? (
    <>
      <Flex alignItems="flex-start" flexDirection="column" mt="6">
        <Text
          color="text.heading"
          mb="1"
          fontWeight="bold"
          fontSize={['base', null, 'md']}
          textStyle="heading"
        >
          {isPackagePageV3Enabled
            ? 'Rooms in other package options'
            : 'Other room options'}
        </Text>
        <Text color="text.heading" lineHeight="1.5" fontSize="sm" mb="4">
          These rooms are applicable to another package option
        </Text>
        <RoomList
          data-testid="OTHER_ROOMS"
          roomOptions={visibleRooms}
          roomIndexOffset={roomIndexOffset}
          {...(isPackagePageV3Enabled ? { onCtaClick: handleCTAClick } : {})}
        />
        {!isPackagePageV3Enabled && !!hiddenRoomCount && !isExpanded && (
          <Button
            data-testid="MORE_ROOMS_BUTTON"
            mt="6"
            py="10px"
            px="8"
            width={['100%', null, '342px']}
            variant="auxiliary"
            onClick={toggleVisibleRooms}
          >
            <Text fontSize="sm">
              Show {hiddenRoomCount} more {pluralize('rooms', hiddenRoomCount)}
            </Text>
          </Button>
        )}
      </Flex>
    </>
  ) : null;
};

export default OtherRooms;
